/**
 * People IQ — Org Structure master admin.
 * Manage departments + designations. Requires `manage_org_structure` capability.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasCapability } from "@/lib/permissions";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Trash2, Building2, Briefcase } from "lucide-react";

export default function OrgMasters() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasCapability(user, "manage_org_structure");

  const [deps, setDeps] = useState([]);
  const [degs, setDegs] = useState([]);
  const [newDep, setNewDep] = useState("");
  const [newDeg, setNewDeg] = useState("");

  const load = async () => {
    const [{ data: a }, { data: b }] = await Promise.all([
      api.get("/people/org/departments"),
      api.get("/people/org/designations"),
    ]);
    setDeps(a);
    setDegs(b);
  };
  useEffect(() => { load(); }, []);

  const addDep = async () => {
    if (!newDep.trim()) return;
    try {
      await api.post("/people/org/departments", { name: newDep.trim() });
      setNewDep("");
      load();
      toast.success("Department added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const addDeg = async () => {
    if (!newDeg.trim()) return;
    try {
      await api.post("/people/org/designations", { name: newDeg.trim() });
      setNewDeg("");
      load();
      toast.success("Designation added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const removeDep = async (d) => {
    if (!window.confirm(`Remove department "${d.name}"?`)) return;
    try {
      await api.delete(`/people/org/departments/${d.id}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const removeDeg = async (d) => {
    if (!window.confirm(`Remove designation "${d.name}"?`)) return;
    try {
      await api.delete(`/people/org/designations/${d.id}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="org-masters-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/people")}><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">People IQ</div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mt-0.5">Org Structure</h1>
          <p className="text-sm text-slate-500">Departments &amp; designations powering the employee form.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-[#7C1D3A]" /> Departments</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {canManage && (
              <div className="flex gap-2">
                <Input value={newDep} onChange={(e) => setNewDep(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addDep()} placeholder="e.g. Production" data-testid="new-dept-input" />
                <Button onClick={addDep} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="add-dept-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
              </div>
            )}
            <div className="divide-y divide-slate-100">
              {deps.length === 0 && <div className="text-slate-400 text-xs py-4 text-center">No departments yet.</div>}
              {deps.map((d) => (
                <div key={d.id} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-800">{d.name}</span>
                    {!d.active && <Badge variant="outline" className="text-slate-400">Inactive</Badge>}
                  </div>
                  {canManage && (
                    <Button variant="ghost" size="icon" onClick={() => removeDep(d)} className="text-rose-500 hover:text-rose-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Briefcase className="w-4 h-4 text-[#7C1D3A]" /> Designations</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {canManage && (
              <div className="flex gap-2">
                <Input value={newDeg} onChange={(e) => setNewDeg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addDeg()} placeholder="e.g. Machine Operator" data-testid="new-desig-input" />
                <Button onClick={addDeg} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="add-desig-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
              </div>
            )}
            <div className="divide-y divide-slate-100">
              {degs.length === 0 && <div className="text-slate-400 text-xs py-4 text-center">No designations yet.</div>}
              {degs.map((d) => (
                <div key={d.id} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-800">{d.name}</span>
                    {!d.active && <Badge variant="outline" className="text-slate-400">Inactive</Badge>}
                  </div>
                  {canManage && (
                    <Button variant="ghost" size="icon" onClick={() => removeDeg(d)} className="text-rose-500 hover:text-rose-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
